import React, { useCallback, useRef } from 'react'
import { ArrowCaret, MenuNewWindow } from '@fs/zion-icon'
import { ListItem } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  getPersonName,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
  useSlsHighlightsContext,
  getRelationshipValue,
  getPrimaryRelationshipText,
} from '@fs/zion-sls'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import useRecordOutlineHighlightsEnabled from '../../../../hooks/useRecordOutlineHighlightsEnabled'
import useHoveredRecordOutline from '../../../../hooks/useHoveredRecordOutline'
import {
  getIconByAttachmentStatus,
  getIconBackgroundColor,
  getIconTestId,
} from '../../../../utils/personAttachmentDisplay.utils'
import { usePersonAttachmentData } from '../../../../views/indexing/hooks/usePersonAttachmentData'

/**
 * PersonDisplay
 *
 * Renders a single person as a list item with attachment status icon, name, relationship label,
 * and interaction handlers for selection and highlight hovering.
 *
 * Features:
 * - Shows attachment status via icon (attached, possible match, or no attachment)
 * - Displays person name and relationship to primary person
 * - Highlights person on hover
 * - Handles click to select person
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person - The person object to display
 * @param {Object} [props.record] - Optional record/household object the person belongs to
 * @param {boolean} props.isSelected - Whether this person is currently selected
 * @param {Function} [props.onPersonSelect] - Callback when person is clicked, receives (personId, recordId)
 * @param {string} [props.overline] - Optional overline text shown above the name (e.g. "On image 7").
 * @param {Function} [props.openNewPage] - When provided, the row opens this callback instead of selecting the
 *   person, and the end element becomes an open-in-new-window action. Used for names on other images.
 * @returns {JSX.Element|null} The person list item or null if person is undefined
 */
const PersonDisplay = ({ person, record, isSelected, onPersonSelect = () => {}, overline, openNewPage }) => {
  const [t] = useTranslation()
  const { types } = useSlsStateContext()
  const r2State = useR2StateContext()
  const recordOutlineEnabled = useRecordOutlineHighlightsEnabled()
  const { hoverRecord, unhoverRecord } = useHoveredRecordOutline()
  const { hoverHighlights, unHover } = useSlsHighlightsContext()
  const listItemRef = useRef()
  const r2SelectedPersonIdRef = useRef()

  if (isSelected && r2SelectedPersonIdRef.current !== r2State.selectedPersonId && listItemRef.current) {
    r2SelectedPersonIdRef.current = r2State.selectedPersonId
  }

  const { attachment, match } = usePersonAttachmentData(person?.id)

  const personName = getPersonName({ person, justOneName: true })
  const relationshipToPrimary = getRelationshipValue(person, types, record) || ''
  const relationshipOrPrimaryLabel = getRelationshipToPrimaryPersonText(person, types, record, t) || ''
  const relationshipLabel = getPrimaryRelationshipText(relationshipOrPrimaryLabel, relationshipToPrimary)

  // Always show icon based on attachment status
  const IconComponent = getIconByAttachmentStatus(attachment, match)
  const iconBackground = getIconBackgroundColor(attachment, match)
  const iconTestId = getIconTestId(attachment, match)
  const startElement = <ListItem.Icon Icon={IconComponent} background={iconBackground} data-testid={iconTestId} />

  const handleClick = useCallback(() => {
    if (openNewPage) {
      openNewPage()
      return
    }
    onPersonSelect(person?.id, record?.id)
  }, [openNewPage, onPersonSelect, person?.id, record?.id])

  // The record outline is staged by SideSheet's shared highlight effect (keyed off
  // r2State.highlightedRecordId) rather than written to a highlight API directly here, since
  // zion-sls's own internal sync effect overwrites direct writes - see useDisplayHighlights.js.
  const handleMouseEnter = useCallback(() => {
    hoverHighlights({ elementIds: [person?.id] })
    // `record` is optional (floating persons are rendered without one), so fall back to the empty
    // string the reducer starts with rather than putting undefined into R2 state.
    if (recordOutlineEnabled) hoverRecord(record?.id ?? '')
  }, [hoverHighlights, person?.id, record?.id, recordOutlineEnabled, hoverRecord])

  const handleMouseLeave = useCallback(() => {
    unHover()
    if (recordOutlineEnabled) unhoverRecord()
  }, [unHover, recordOutlineEnabled, unhoverRecord])

  const endelementCaretButtonTitle = openNewPage
    ? t('person-display.button.open-new-tab', 'Open {personName} in a new tab', { personName })
    : `${t('person-display.button.person-select', 'Click to view')} ${personName}`

  const endElement = (
    <ListItem.IconButton
      Icon={openNewPage ? MenuNewWindow : ArrowCaret}
      color="gray"
      title={endelementCaretButtonTitle}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )

  if (!person) return null

  return (
    <ListItem
      ref={listItemRef}
      data-testid={personName}
      key={person?.id}
      overline={overline}
      primaryText={personName}
      secondaryText={relationshipLabel}
      emphasized={!!person?.primary}
      startElement={startElement}
      endElement={endElement}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      selected={isSelected}
    />
  )
}

export default PersonDisplay
